const LENGTH_LABELS = {
  one_two_sentences: "One or two sentences",
  short_paragraph: "A short paragraph",
  few_paragraphs: "A few paragraphs",
};

const TONE_LABELS = {
  direct_warm: "Direct and warm",
  polished_formal: "Polished and formal",
  casual_playful: "Casual and playful",
};

function extractContactContext(contact) {
  if (!contact) return { name: "", company: "", role: "", email: "", phone: "", notes: "", status: "", tags: [], social: [], specialties: "", markets: "" };

  const identity = contact.identity || {};
  const reach = contact.reach || {};
  const person = identity.person || {};
  const company = identity.company || {};

  const name = [person.firstName, person.lastName].filter(Boolean).join(" ") || "";
  const companyName = company.name || "";
  const role = person.title || person.role || company.title || "";

  const emails = Array.isArray(reach.emails) ? reach.emails : [];
  const email = emails.find((e) => e?.value?.trim())?.value || "";

  const phones = Array.isArray(reach.phones) ? reach.phones : [];
  const phone = phones.find((p) => p?.value?.trim())?.value || "";

  const social = Array.isArray(reach.social) ? reach.social : [];
  const status = contact.status || "";
  const data = contact.data || {};
  const legacy = data.identityLegacy || {};
  const tags = Array.isArray(data.tags) ? data.tags.filter(Boolean) : (Array.isArray(contact.tags) ? contact.tags.filter(Boolean) : []);
  const notes = legacy.notes || identity.notes || "";
  const specialties = legacy.specialties || identity.specialties || "";
  const markets = legacy.markets || identity.markets || "";

  return { name, company: companyName, role, email, phone, notes, status, tags, social, specialties, markets };
}

/**
 * Build the system + user prompt for email generation via WebLLM.
 *
 * @param {object} params
 * @param {object} params.voiceProfile      — VoiceProfile from IndexedDB
 * @param {object} params.contact           — Full contact object
 * @param {string} params.purpose           — What the email should accomplish
 * @param {string} [params.companyKnowledge] — Company context to include
 * @returns {{ system: string, user: string }}
 */
export function assembleEmailPrompt({ voiceProfile, contact, purpose, companyKnowledge, senderFirstName }) {
  const ctx = extractContactContext(contact);
  const contactName = ctx.name || "the contact";

  const system = [];

  const senderName = senderFirstName || "";

  system.push(
    "You are drafting an email on behalf of the user.",
    "Write a SUBSTANTIAL, detailed email — at least 3-4 paragraphs. Do NOT write a short or generic email.",
    "ONLY use facts provided in the contact context and company knowledge below. Do NOT invent or assume any details not given.",
    "You MUST incorporate the COMPANY KNOWLEDGE section below into the email body — reference the sender's company, products, or services where relevant. This is critical.",
    "",
    "SIGNATURE RULES (STRICTLY FOLLOW):",
    "- The ONLY thing after the email body is a simple signoff and first name. Nothing else.",
    "- NEVER include: email address, phone number, company name, job title, website, or any contact info in the signature.",
    "- NEVER include [Your Full Name], [Company Name], [Your Email], or any placeholder text.",
    "- NEVER add lines like 'Email: ...' or 'Phone: ...' at the end.",
    `- The last two lines of the email must be EXACTLY like this example:`,
    `  Best,`,
    `  ${senderName || "Alex"}`,
    "- Nothing after the first name. No extra lines. No contact details.",
  );

  // Company knowledge
  if (companyKnowledge?.trim()) {
    system.push(
      "",
      "COMPANY KNOWLEDGE (about the sender's company/products — use this when referencing what you/your company does):",
      companyKnowledge.trim(),
    );
  } else {
    system.push(
      "",
      "NOTE: No company knowledge has been provided. Do NOT mention the sender's company name, products, or services. Keep the email focused on the recipient and the stated purpose. Never say 'no information' or reference missing data — simply omit company details.",
    );
  }

  // Voice profile settings
  const hasVoiceTraining = voiceProfile?.formality || voiceProfile?.toneArchetype;

  if (hasVoiceTraining) {
    const greeting = (voiceProfile.greeting || "").replace("[name]", contactName);
    const signoff = voiceProfile.signoff || "";

    system.push(
      "",
      "USER'S TRAINED WRITING STYLE:",
    );
    if (voiceProfile.formality) system.push(`- Formality level: ${voiceProfile.formality}/5`);
    if (voiceProfile.contractions) system.push(`- Contractions: ${voiceProfile.contractions}`);
    if (greeting) system.push(`- Preferred greeting: ${greeting}`);
    if (signoff) system.push(`- Preferred signoff: ${signoff}`);
    if (voiceProfile.length) system.push(`- Typical length: ${LENGTH_LABELS[voiceProfile.length] || voiceProfile.length}`);
    if (voiceProfile.emojis) system.push(`- Emoji use: ${voiceProfile.emojis}`);
    if (voiceProfile.toneArchetype) system.push(`- Tone: ${TONE_LABELS[voiceProfile.toneArchetype] || voiceProfile.toneArchetype}`);

    // Voice samples
    const samples = (voiceProfile.voiceSamples || []).filter((s) => s?.trim());
    if (voiceProfile.voiceSample?.trim()) samples.unshift(voiceProfile.voiceSample.trim());

    if (samples.length > 0) {
      system.push("", `VOICE SAMPLES (${samples.length} example${samples.length > 1 ? "s" : ""} of how the user writes):`);
      samples.forEach((s, i) => {
        system.push(`--- Sample ${i + 1} ---`);
        system.push(`"${s}"`);
      });
      system.push("", "MATCH THIS VOICE CLOSELY. Do not sound generic or AI-like.");
    }

    if (voiceProfile.customInstructions?.trim()) {
      system.push("", "CUSTOM INSTRUCTIONS:", voiceProfile.customInstructions);
    }
  }

  // Use edited prompt preview if the user customized it
  if (voiceProfile?.editedPromptPreview?.trim()) {
    return {
      system: voiceProfile.editedPromptPreview,
      user: buildUserMessage(ctx, contactName, purpose),
    };
  }

  return {
    system: system.join("\n"),
    user: buildUserMessage(ctx, contactName, purpose),
  };
}

function buildUserMessage(ctx, contactName, purpose) {
  const user = ["CONTACT INFORMATION:"];
  user.push(`Name: ${contactName}`);
  if (ctx.company) user.push(`Company: ${ctx.company}`);
  if (ctx.role) user.push(`Role: ${ctx.role}`);
  if (ctx.email) user.push(`Email: ${ctx.email}`);
  if (ctx.phone) user.push(`Phone: ${ctx.phone}`);
  if (ctx.status) user.push(`Status: ${ctx.status}`);
  if (ctx.tags.length > 0) user.push(`Tags: ${ctx.tags.join(", ")}`);
  if (ctx.specialties) user.push(`Specialties: ${ctx.specialties}`);
  if (ctx.markets) user.push(`Markets: ${ctx.markets}`);

  ctx.social.forEach((s) => {
    if (s?.value) user.push(`${s.type || "social"}: ${s.value}`);
  });

  if (ctx.notes) {
    user.push("", "NOTES ABOUT THIS CONTACT:", ctx.notes);
  }

  user.push("", `EMAIL PURPOSE:\n${purpose}`);
  user.push(
    "",
    "Write the email now. Output EXACTLY in this format with no other text:",
    "",
    "Subject: <subject line here>",
    "Body: <email body here>",
  );

  return user.join("\n");
}

export const GENERATION_CONFIG = {
  temperature: 0.7,
  top_p: 0.9,
  max_tokens: 1024,
  repetition_penalty: 1.1,
};

/**
 * Generate an email using the WebLLM engine with streaming.
 */
export async function generateEmailWithWebLLM(engine, params, onChunk) {
  const { system, user } = assembleEmailPrompt(params);

  const messages = [
    { role: "system", content: system },
    { role: "user", content: user },
  ];

  let full = "";
  let usage = null;

  const chunks = await engine.chat.completions.create({
    messages,
    ...GENERATION_CONFIG,
    stream: true,
    stream_options: { include_usage: true },
  });

  for await (const chunk of chunks) {
    const delta = chunk.choices?.[0]?.delta?.content || "";
    if (delta) {
      full += delta;
      if (onChunk) onChunk(delta, full);
    }
    if (chunk.usage) usage = chunk.usage;
  }

  return { text: full, usage };
}

/**
 * Parse the generated email text into subject and body.
 */
export function parseGeneratedEmail(text) {
  const raw = (text || "").trim();
  if (!raw) return { subject: "", body: "" };

  const subjectMatch = raw.match(/^Subject:\s*(.+)/im);
  const subject = subjectMatch ? subjectMatch[1].trim() : "";

  // Look for explicit "Body:" marker
  const bodyMatch = raw.match(/^Body:\s*([\s\S]*)/im);
  if (bodyMatch) {
    return { subject, body: bodyMatch[1].trim() };
  }

  // No "Body:" marker — everything after the subject line is the body
  if (subjectMatch) {
    const afterSubject = raw.slice(subjectMatch.index + subjectMatch[0].length).trim();
    return { subject, body: afterSubject };
  }

  // No markers at all — treat the whole thing as body
  return { subject: "", body: raw };
}
